// Drawing a contract as pages, in a frame the caller can reach.
//
// The template is one long HTML document; the PDF is paginated by Chromium.
// On screen, `paginate` breaks it where the template declares a page break and
// `fillPages` carries an overflowing sheet onto the next, so a reader sees A4
// pages rather than a scroll. Shared by the staff draft (App.js, which adds
// its editing affordances in `PAGED_CSS`) and the public new sale page's draft
// (NewSale.js), which shows the pages and nothing else. The why of every rule
// below is docs/decisions/compiled-document.md, "The draft, one page or two".
//
// Nothing here runs inside the frame: the caller reaches into a same-origin
// document and rearranges it, so the document itself needs no script.

// The page layout, injected into the frame — never into the template, which
// reaches every renderer.
export const SHEET_CSS = `
/* Phones and tablets inflate what they judge to be body text, and only that:
 * the 9pt clauses rendered bigger than the 10pt headings above them. The
 * portal's own 'index.css' turns this off, but the preview is a frame with a
 * document of its own and inherits none of it. */
html { -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
  /* The desk the pages sit on: the compiled viewer's own colour, so switching
     between Compiled and Draft does not change the ground under the page.
     The sheet's shadow is what separates the paper from it. */
  body { background: #f4f5f7 !important; padding: 18pt 0 !important; }
  .contract { width: auto !important; }
  .sheet {
    width: 595.3pt; min-height: 841.9pt; box-sizing: border-box;
    margin: 0 auto 14pt; padding: 42.5pt 32.2pt;
    background: #fff; box-shadow: 0 1px 5px rgba(0,0,0,0.20);
  }
  .sheet-flush { padding: 0; overflow: hidden; }
  .page-break { display: none !important; }
  .cover { margin-bottom: 0 !important; }
`;

export function paginate(doc) {
  const contract = doc.querySelector(".contract");
  if (!contract || contract.dataset.paged) return;
  contract.dataset.paged = "1";

  const sheets = [];
  const sheet = (flush) => {
    const el = doc.createElement("div");
    el.className = flush ? "sheet sheet-flush" : "sheet";
    sheets.push(el);
    return el;
  };

  let current = null;

  const place = (child) => {
    // A cover is already a whole page and brings its own padding.
    if (child.classList.contains("cover")) {
      sheet(true).appendChild(child);
      current = null;
      return;
    }
    // A section carries its own margins and its breaks sit *inside* it, so it
    // is cloned per break rather than moved whole — otherwise every break in
    // the document would be invisible, which is most of them.
    if (child.classList.contains("section")) {
      let clone = child.cloneNode(false);
      (current || (current = sheet(false))).appendChild(clone);
      for (const node of [...child.childNodes]) {
        if (node.nodeType === 1 && node.classList.contains("page-break")) {
          clone = child.cloneNode(false);
          current = sheet(false);
          current.appendChild(clone);
          continue;
        }
        clone.appendChild(node);
      }
      child.remove();
      return;
    }
    // A break at the top level, outside any section, still ends the page.
    if (child.classList.contains("page-break")) {
      child.remove();
      current = null;
      return;
    }
    // **A wrapper is walked into, not appended whole.** The contract's tail —
    // the guarantee, the vendor statement and its due diligence checklist —
    // lives inside `<div class="unfooted">`, which exists so the PDF stops
    // footering at the end of the contract proper. Moved as one node, every
    // break inside it became invisible and the whole tail rendered as a single
    // enormous sheet: the guarantee ran into the statement, and the statement
    // into the checklist. The PDF was never wrong, because Chromium paginates
    // for itself — only the preview, which paginates on what the document
    // declares.
    if (child.querySelector && child.querySelector(".section, .page-break")) {
      for (const grandchild of [...child.children]) place(grandchild);
      child.remove();
      return;
    }
    (current || (current = sheet(false))).appendChild(child);
  };

  for (const child of [...contract.children]) place(child);
  sheets.forEach((el) => contract.appendChild(el));
}

// ── Pages the document does not declare ──────────────────────────────
// `paginate` breaks only where the template says to, and the special and
// general conditions say nowhere: Word flowed them across a dozen pages by
// itself, and Chromium does the same when the PDF is drawn. So in the draft
// each of them was one sheet several metres tall, which read as a document
// that had stopped paginating — and beside a normal page in the two-page view
// it was impossible to miss.
//
// This fills each sheet to an A4 page and carries the rest onto the next one,
// the way a renderer would. **It measures, so it runs on a laid-out, unscaled
// frame**: after `paginate` and before `applyPageLayout`, with any two-page
// scale taken off first. A frame that is hidden measures nothing, so it is
// skipped and left for the relayout that runs when it is shown — `filled` is
// what says it still has to happen.
//
// It breaks *between* blocks, never inside one: a paragraph, a table or a
// clause moves whole. A block taller than a page on its own stays where it is
// rather than being split, since a cut through a table is worse than one long
// sheet — and the PDF is the document anyway; this is the screen that edits it.
//
// The sections it moves across are shallow-copied onto the new sheet, so each
// page keeps its section's own column and margin styles.
export const SHEET_FILL_PX = { height: 841.9 * 4 / 3, pad: 42.5 * 4 / 3 };
const isPageContainer = (el) => el.classList?.contains("section") || el.tagName === "SECTION";
const pageBlocks = (container, out = []) => {
  for (const child of container.children) {
    if (isPageContainer(child)) pageBlocks(child, out);
    else out.push(child);
  }
  return out;
};
const moveTailFrom = (sheet, block, next) => {
  const path = [];
  for (let n = block; n && n !== sheet; n = n.parentNode) path.unshift(n);
  let target = next;
  path.forEach((node, depth) => {
    const followers = [];
    for (let n = node.nextSibling; n; n = n.nextSibling) followers.push(n);
    if (depth === path.length - 1) {
      target.appendChild(node);
      followers.forEach((f) => target.appendChild(f));
    } else {
      const clone = node.cloneNode(false);
      target.appendChild(clone);
      followers.forEach((f) => target.appendChild(f));
      target = clone;
    }
  });
};
export const fillPages = (doc) => {
  const contract = doc?.querySelector?.(".contract");
  if (!contract || contract.dataset.filled) return false;
  // **Only a paginated document, and only once it measures.** The relayout
  // runs off a ResizeObserver, which fires while the frame is still parsing —
  // a `.contract` already exists then, with no sheets in it. Filling that did
  // nothing and still stamped `filled`, so the fill after pagination stood
  // down and the special and general conditions stayed one sheet each, on
  // whichever loads lost the race.
  if (!contract.dataset.paged) return false;
  if (!doc.documentElement.clientWidth) return false;   // hidden: nothing to measure
  const first = contract.querySelector(".sheet");
  if (!first || !first.getBoundingClientRect().height) return false;
  // Unscaled, one column — the measurements are against a real A4 sheet.
  const hadTwoUp = doc.body.classList.contains("s32-two-up");
  const zoom = contract.style.zoom;
  doc.body.classList.remove("s32-two-up");
  contract.style.zoom = "";
  let guard = 2000;
  for (let sheet = contract.querySelector(".sheet"); sheet && guard-- > 0; sheet = sheet.nextElementSibling) {
    if (sheet.classList.contains("sheet-flush")) continue;
    const top = sheet.getBoundingClientRect().top;
    const limit = top + SHEET_FILL_PX.height - SHEET_FILL_PX.pad;
    const blocks = pageBlocks(sheet);
    let at = blocks.findIndex((b) => b.getBoundingClientRect().bottom > limit + 0.5);
    // The first block alone overflowing is one taller than a page: it keeps
    // the sheet to itself and what follows it starts the next.
    if (at === 0) at = blocks.length > 1 ? 1 : -1;
    if (at < 1) continue;
    const next = doc.createElement("div");
    next.className = "sheet";
    sheet.after(next);
    moveTailFrom(sheet, blocks[at], next);
  }
  // Tidy the copies a move left with nothing in them.
  contract.querySelectorAll(".sheet .section, .sheet section").forEach((el) => {
    if (!el.children.length && !el.textContent.trim()) el.remove();
  });
  if (hadTwoUp) doc.body.classList.add("s32-two-up");
  contract.style.zoom = zoom;
  contract.dataset.filled = "1";
  return true;
};
